
import React from "react";
import { demoPlayers } from "@/components/home/types/demo-types";
import { GlassCard } from "@/components/ui/glass-card";
import { Medal } from "lucide-react";
import { cn } from "@/lib/utils";
import { playButtonDownSound, playButtonUpSound, triggerHapticFeedback } from "@/utils/effects";
import { Ripple } from "@/components/ui/ripple-effect";

interface DemoPlayerListProps {
  demoPlayers: typeof demoPlayers;
  selectedPlayers: string[];
  onPlayerSelect: (playerId: string) => void;
}

export function DemoPlayerList({
  demoPlayers,
  selectedPlayers,
  onPlayerSelect
}: DemoPlayerListProps) {
  
  const getPlayerPoints = (playerId: string): number | null => {
    // Find position in selectedPlayers array
    const index = selectedPlayers.indexOf(playerId);
    if (index === -1) return null;
    
    // Calculate points: first = 3, second = 2, third = 1
    return 3 - index;
  };

  const getMedalColor = (points: number | null): string => {
    if (points === 3) return "text-yellow-500";
    if (points === 2) return "text-gray-400";
    if (points === 1) return "text-amber-700";
    return "";
  };
  
  const getBgColor = (points: number | null) => {
    if (points === null) return "bg-white dark:bg-gray-800/90"; // Clean background for unselected
    if (points === 3) return "podium-gold"; // Gold - use CSS class
    if (points === 2) return "podium-silver"; // Silver - use CSS class
    if (points === 1) return "podium-bronze"; // Bronze - use CSS class
    return "bg-white dark:bg-gray-800/90"; // Default
  };

  const handlePlayerClick = (playerId: string) => {
    // Play sounds on selection
    playButtonDownSound();
    setTimeout(() => playButtonUpSound(), 100);
    
    // Use selection haptic pattern
    triggerHapticFeedback('selection');
    
    // Call the selection handler
    onPlayerSelect(playerId);
  };

  return (
    <GlassCard className="p-0 overflow-hidden mb-2 sm:mb-3 max-w-md mx-auto w-full border-0 shadow-md bg-white/95 dark:bg-gray-800/95">
      <div className="divide-y divide-gray-200/30 dark:divide-gray-700/30">
        {demoPlayers.map((player) => {
          const points = getPlayerPoints(player.id);
          const isSelected = points !== null;
          const medalColor = getMedalColor(points);
          
          return (
            <div
              key={player.id}
              onClick={() => handlePlayerClick(player.id)}
              className={cn(
                "flex items-center justify-between cursor-pointer transition-colors p-3",
                getBgColor(points),
                isSelected 
                  ? "hover:bg-opacity-90" 
                  : "hover:bg-gray-50 dark:hover:bg-gray-800/50"
              )}
              data-player-id={player.id}
            >
              <div className="flex-1">
                <div className="font-medium">{player.name}</div>
              </div>
              
              {isSelected && (
                <div className={`flex items-center gap-1 ${medalColor}`}>
                  <Medal className="h-4 w-4" />
                  <span className="font-bold">{points}</span>
                </div>
              )}
              
              {/* Add ripple effect for better interactivity */}
              <Ripple color="rgba(27, 171, 220, 0.7)" />
            </div>
          );
        })}
      </div>
    </GlassCard>
  );
}
